import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMatch } from "react-router-dom";
import UploadSlideOver from "../../components/UploadSlideOver";
import { UploadRequest } from "./types";

/** True while the drag carries at least one file (not e.g. dragged text/links). */
const dragHasFiles = (e: DragEvent): boolean =>
  Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * A dialog is already up (our own, or any other MUI Dialog the page opened)
 * — let it and its own drop zone handle the drag instead of layering the
 * page-wide prompt and a second upload dialog on top of it.
 */
const isDialogOpen = (): boolean =>
  document.querySelector('[role="dialog"]') !== null;

interface UploadContextValue {
  openUpload: (request?: UploadRequest) => void;
}

const UploadContext = React.createContext<UploadContextValue | null>(null);

/** Opens the upload slide-over from anywhere under GlobalUploadProvider. */
export function useUpload(): UploadContextValue {
  const ctx = React.useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUpload must be used inside GlobalUploadProvider");
  }
  return ctx;
}

/**
 * Makes the whole page a drop target for a build file: dragging one in shows
 * a full-page prompt, and dropping it opens the upload slide-over with the file
 * already in hand. Mount once around any authenticated layout — every screen
 * underneath gets the same behavior for free, with no per-page wiring.
 */
export const GlobalUploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dragActive, setDragActive] = React.useState(false);
  const [upload, setUpload] = React.useState<{
    open: boolean;
    request: UploadRequest;
    requestId: number;
  }>({ open: false, request: {}, requestId: 0 });
  const projectMatch = useMatch("/dashboard/:projectId");
  const dragCounter = React.useRef(0);

  const openUpload = React.useCallback(
    (request: UploadRequest = {}) => {
      if (upload.open) return;
      setUpload((prev) => ({
        open: true,
        request: {
          ...request,
          projectId: request.projectId ?? projectMatch?.params.projectId,
        },
        requestId: prev.requestId + 1,
      }));
    },
    [upload.open, projectMatch?.params.projectId]
  );
  const openUploadRef = React.useRef(openUpload);
  openUploadRef.current = openUpload;

  React.useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      if (isDialogOpen()) return;
      dragCounter.current += 1;
      setDragActive(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!dragHasFiles(e)) return;
      // Always prevented, even with a dialog open — otherwise the browser
      // navigates to the file instead of the in-page dialog's own drop zone
      // ever seeing the drop.
      e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (!dragHasFiles(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setDragActive(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragCounter.current = 0;
      setDragActive(false);
      if (isDialogOpen()) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) openUploadRef.current({ file });
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return (
    <UploadContext.Provider value={{ openUpload }}>
      {children}

      {dragActive && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            border: "4px dashed",
            borderColor: "primary.main",
            borderRadius: 2,
            m: 1.5,
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              backgroundColor: "background.paper",
              px: 4,
              py: 2,
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            Drop a file in this box to upload
          </Typography>
        </Box>
      )}

      {/* The key remounts the slide-over per open so its form state is fresh;
          a closed instance stays mounted until the next open to keep its
          snackbar. */}
      <UploadSlideOver
        key={upload.requestId}
        open={upload.open}
        request={upload.request}
        onClose={() => setUpload((prev) => ({ ...prev, open: false }))}
      />
    </UploadContext.Provider>
  );
};

export default GlobalUploadProvider;
